import {
  Component,
  ChangeDetectorRef,
  OnInit,
  ViewChild,
  inject,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { User, Checkout, Supplier, Depot, ProductMovePurpose } from 'depoto-core/src/entities'
import { ExportsComponent } from '../exports/exports.component'
import { productMovePacksColumns } from '../../utils/definitions.util'
import { SchemaUtil } from '../../utils'
import { CheckoutService } from '../../services/entity-services/checkout.service'
import { DepotService } from '../../services/entity-services/depot.service'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { TagService } from '../../services/entity-services/tag.service'
import { ProductMovePackService } from '../../services/entity-services/productMovePack.service'
import { ProductMoveService } from '../../services/entity-services/productMove.service'
import { SupplierService } from '../../services/entity-services/supplier.services'
import { UserService } from '../../services/entity-services/user.service'

const DEFAULT_FILTERS = {
  type: 'all',
  depotFrom: null,
  depotTo: null,
  checkout: null,
  tags: [],
  tagsNegate: [],
  withoutCheckout: false,
  fulltext: '',
  orderFulltext: '',
  externalReferenceFulltext: '',
  dateFrom: '',
  dateTo: '',
  withOrder: 'all',
  createdBy: '',
  productFulltext: '',
  number: '',
  supplier: null,
  purpose: 'all',
}

@Component({
  selector: 'product-move-packs-page',
  templateUrl: 'product-move-packs.component.html',
  styleUrls: ['product-move-packs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductMovePacksComponent extends BaseListing implements OnInit {
  private productMovePackService = inject(ProductMovePackService)
  private productMoveService = inject(ProductMoveService)
  private checkoutService = inject(CheckoutService)
  private depotService = inject(DepotService)
  private tagService = inject(TagService)
  private supplierService = inject(SupplierService)
  private userService = inject(UserService)

  @ViewChild('exportsModal') exportsModal: ExportsComponent

  listingColumns = signal(productMovePacksColumns)

  operationTypes: string[] = ['all', 'in', 'out', 'transfer', 'reservation']
  hasOrderTypes: string[] = ['all', 'yes', 'no']

  filters = signal(structuredClone(DEFAULT_FILTERS))
  filtersExpanded = signal(false)
  readonly defaultFilters = DEFAULT_FILTERS
  readonly advancedFilterKeys = [
    'tags',
    'tagsNegate',
    'checkout',
    'withOrder',
    'orderFulltext',
    'createdBy',
    'number',
    'productFulltext',
    'supplier',
    'purpose',
  ]

  sortProp = signal<string>(
    this.core.services.storage.getSync(this.getStorageKeyForEntity('productMovePacks'))?.sortProp
      ? this.core.services.storage.getSync(this.getStorageKeyForEntity('productMovePacks'))?.sortProp
      : 'created'
  )

  sortDir = signal<'asc' | 'desc'>(
    this.core.services.storage.getSync(this.getStorageKeyForEntity('productMovePacks'))?.sortDir
      ? this.core.services.storage.getSync(this.getStorageKeyForEntity('productMovePacks'))?.sortDir
      : 'desc'
  )

  readonly queryCheckouts = injectQuery(() => ({
    queryKey: ['checkouts', 'options'],
    queryFn: async () => {
      return await this.checkoutService
        .getCheckouts({ page: 1, sort: 'name', direction: 'asc' })
        .then(result => result.items)
    },
    select: data => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.checkouts?.length) {
        // TODO CORE USER
        return []
      } else if (
        !this.checkoutService.doesUserHaveAllCheckouts(data as any[]) &&
        this.core.services.user.user?.checkouts?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.checkouts // TODO CORE USER
      } else {
        return data
      }
    },
  }))

  readonly queryUsers = injectQuery(() => ({
    queryKey: ['users', 'options'],
    queryFn: async () => {
      return await this.userService
        .getUsers(
          { page: 1, sort: 'lastName', direction: 'asc' },
          {
            id: null,
            name: null,
          }
        )
        .then(result => {
          return result?.items
        })
    },
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers', 'options'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers({ sort: 'name', direction: 'asc' }, { id: null, name: null })
        .then(r => r.items)
    },
  }))

  readonly queryPurposesOptions = injectQuery(() => ({
    queryKey: ['productMovePurposes', 'options'],
    queryFn: async () => {
      return await this.productMoveService
        .getProductMovePurposes({ sort: 'name', direction: 'asc' }, { id: null, name: null })
        .then(r => r.items)
    },
  }))

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) return [] // TODO CORE USER
      return await this.depotService
        .getDepots(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null, enabled: null }
        )
        .then(result => result.items)
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  depots = computed(() => {
    const d = (this.queryDepotsOptions.data() ?? []) as Depot[]
    return [new Depot({ id: 0, name: 'all' }), ...d]
  })
  users = computed(() => {
    const u = (this.queryUsers.data() ?? []) as User[]
    return [new User({ id: 0, name: 'all', email: 'all' }), ...u]
  })
  suppliers = computed(() => {
    const s = (this.querySuppliers.data() ?? []) as Supplier[]
    return [new Supplier({ id: 0, name: 'all' } as any), ...s]
  })
  purposes = computed(() => {
    const p = (this.queryPurposesOptions.data() ?? []) as ProductMovePurpose[]
    return [new ProductMovePurpose({ id: 'all', name: 'all' } as any), ...p]
  })
  checkouts = computed(() => {
    const ch = (this.queryCheckouts.data() ?? []) as Checkout[]
    return [
      new Checkout({ id: 0, name: 'all' } as any),
      new Checkout({ id: 'withoutCheckout', name: 'withoutCheckout' } as any),
      ...ch,
    ]
  })

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService.getTags({ sort: 'name' }, { id: null, name: null, type: null }).then(r => r.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'product_move_pack'),
  }))

  readonly filtersForAdvancedVisibility = computed(() => {
    const filters = { ...this.filters() }
    if (!this.isCheckoutFilterActive(filters.checkout)) {
      filters.checkout = DEFAULT_FILTERS.checkout
    }
    return filters
  })

  readonly queryFilters = computed(() => {
    const v = this.filters()
    const options: any = {}

    if (v.type !== 'all' && v.type) options.type = v.type
    if (+v.depotFrom > 0) options.depotFrom = v.depotFrom
    if (+v.depotTo > 0) options.depotTo = v.depotTo
    if (v.tags?.length) options.tags = v.tags
    if (v.tagsNegate?.length) options.tagsNegate = v.tagsNegate
    if (v.fulltext?.length) options.fulltext = v.fulltext
    if (v.number?.length) options.number = v.number
    if (v.orderFulltext?.length) options.orderFulltext = v.orderFulltext
    if (v.externalReferenceFulltext?.length) options.orderFulltext = v.externalReferenceFulltext
    if (v.withOrder && v.withOrder !== 'all') options.withOrder = v.withOrder === 'yes'
    if (v.productFulltext?.length) options.productFulltext = v.productFulltext
    if (v.supplier) options.supplier = v.supplier
    if (v.createdBy) options.createdBy = v.createdBy
    if (v.purpose && v.purpose !== 'all') options.purpose = v.purpose

    if (v.type !== 'transfer') {
      if (v.checkout === 'withoutCheckout') {
        options.withoutCheckout = true
      } else if (
        v.checkout &&
        v.checkout !== 'all' &&
        v.checkout !== 'withoutCheckout' &&
        !isNaN(+v.checkout) &&
        +v.checkout > 0
      ) {
        options.checkouts = [v.checkout]
      }
    }

    if (v.dateFrom?.length || v.dateTo?.length) {
      options.created = {
        ...(v.dateFrom?.length ? { left_date: v.dateFrom } : {}),
        ...(v.dateTo?.length ? { right_date: v.dateTo + 'T23:59:59' } : {}),
      }
    }

    return options
  })

  readonly queryProductMovePacks = injectInfiniteQuery(() => ({
    queryKey: ['productMovePacks', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      // this.saveViewSettings('productMovePacks', {
      //   filters: this.filters(),
      //   sortProp: this.sortProp(),
      //   sortDir: this.sortDir(),
      // })
      return await this.productMovePackService
        .getProductMovePacks(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          SchemaUtil.productMovePackList
        )
        .then(result => {
          this.saveCurrentView()
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  totalItems = computed(() => this.queryProductMovePacks.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  areCoreDataLoading = computed(() => {
    return (
      this.queryCheckouts.isFetching() ||
      this.queryDepotsOptions.isFetching() ||
      this.queryTags.isFetching() ||
      this.queryCheckouts.isLoading() ||
      this.queryProductMovePacks.isLoading()
    )
  })

  constructor(
    protected core: CoreService,
    protected changeDetector: ChangeDetectorRef
  ) {
    effect(() => {
      this.queryTags.data()
      this.queryCheckouts.data()
      this.queryDepotsOptions.data()
      this.queryUsers.data()
      this.querySuppliers.data()
      this.queryPurposesOptions.data()
    })
    if (window.location.pathname === '/product-move-pack') {
      window.history.replaceState(null, document.title, '/product-move-packs')
    }
    super(core)
  }

  async ngOnInit() {
    const view = this.loadViewSettings('productMovePacks')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'created')
    this.sortDir.set(view?.sortDir || 'desc')
    this.filters.update(f => ({ ...f, createdBy: f.createdBy !== '0' ? f.createdBy : '' }))
  }

  setFilter(key: string, value: any) {
    this.filters.update(f => ({ ...f, [key]: value }))
  }

  applyFilters(): void {
    this.saveCurrentView()
    this.queryProductMovePacks.refetch()
  }

  clearFilters(type?: string): void {
    if (type === 'depots') {
      this.filters.update(f => ({ ...f, depotFrom: null, depotTo: null }))
      this.saveCurrentView()
      this.queryProductMovePacks.refetch()
      return
    }
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.saveCurrentView()
    this.queryProductMovePacks.refetch()
  }

  downloadReport(report: any) {
    this.core.services.download.downloadDocumentAndShowInNewWindow(report.url)
  }

  private isCheckoutFilterActive(checkout: any): boolean {
    return (
      checkout === 'withoutCheckout' ||
      (checkout && checkout !== 'all' && checkout !== 'withoutCheckout' && !isNaN(+checkout) && +checkout > 0)
    )
  }

  private saveCurrentView(): void {
    this.saveViewSettings('productMovePacks', {
      filters: this.filters(),
      sortProp: this.sortProp(),
      sortDir: this.sortDir(),
    })
  }

  protected readonly DEFAULT_FILTERS = DEFAULT_FILTERS
}
